import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

const STORAGE_KEY = 'cz:admin_company_id'
const STORAGE_NAME = 'cz:admin_company_name'
const STORAGE_SINCE = 'cz:admin_since'
/** Sessão de impersonation expira em 4h — evita acesso indefinido a dados de cliente */
const IMPERSONATION_TTL_MS = 4 * 60 * 60 * 1000

/**
 * Grava evento de auditoria de impersonation em runtime_logs (persistente).
 * Await-ado antes do reload para não perder o registro; falha não bloqueia.
 */
async function auditImpersonation(action: 'enter' | 'exit' | 'expired', companyId: string | null, companyName?: string | null) {
  try {
    const { data } = await supabase.auth.getUser()
    // Mesmo shape usado pelo pipeline de observability (runtime_logs)
    await supabase.from('runtime_logs').insert([{
      level:    'info',
      category: 'admin_impersonation',
      message:  `Impersonation ${action}: ${companyName ?? companyId ?? '?'}`,
      context:  { action, target_company_id: companyId, target_company_name: companyName ?? null, admin_user_id: data.user?.id ?? null, admin_email: data.user?.email ?? null },
    }])
  } catch { /* auditoria é best-effort — não bloqueia a operação */ }
}

interface ImpersonationContextType {
  /** ID da empresa que o Super Admin está visualizando, ou null */
  viewingCompanyId: string | null
  viewingCompanyName: string | null
  /** Indica se o usuário atual é platform admin (carregado de RPC) */
  isPlatformAdmin: boolean
  isReady: boolean
  enterAsCompany: (companyId: string, companyName: string) => void
  exitImpersonation: () => void
}

const Ctx = createContext<ImpersonationContextType | null>(null)

export function useAdminImpersonation() {
  const c = useContext(Ctx)
  if (!c) throw new Error('useAdminImpersonation must be used within AdminImpersonationProvider')
  return c
}

/**
 * Lê o impersonation atual SEM precisar do contexto (útil em hooks de baixo nível).
 * Retorna null se não houver.
 */
export function readImpersonationCompanyId(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY) || null
  } catch {
    return null
  }
}

/** Limpa impersonation expirada (TTL 4h) ANTES do estado inicial ser lido. */
function clearIfExpired(): void {
  try {
    const id = localStorage.getItem(STORAGE_KEY)
    if (!id) return
    const since = Number(localStorage.getItem(STORAGE_SINCE) ?? 0)
    // since ausente (sessão anterior à introdução do TTL) também expira
    if (!since || Date.now() - since > IMPERSONATION_TTL_MS) {
      const name = localStorage.getItem(STORAGE_NAME)
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(STORAGE_NAME)
      localStorage.removeItem(STORAGE_SINCE)
      localStorage.removeItem('cz:company_id')
      void auditImpersonation('expired', id, name)
    }
  } catch { /* noop */ }
}

export function AdminImpersonationProvider({ children }: { children: React.ReactNode }) {
  const [viewingCompanyId, setViewingCompanyId] = useState<string | null>(() => {
    clearIfExpired()
    try { return localStorage.getItem(STORAGE_KEY) } catch { return null }
  })
  const [viewingCompanyName, setViewingCompanyName] = useState<string | null>(() => {
    try { return localStorage.getItem(STORAGE_NAME) } catch { return null }
  })
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false)
  const [isReady, setIsReady] = useState(false)

  // Verifica se usuário atual é platform admin
  useEffect(() => {
    let cancelled = false
    async function check() {
      try {
        const { data: sessionData } = await supabase.auth.getSession()
        if (!sessionData.session) {
          if (!cancelled) setIsPlatformAdmin(false)
          return
        }

        const { data } = await supabase.rpc('is_platform_admin')
        if (!cancelled) setIsPlatformAdmin(data === true)
      } catch {
        if (!cancelled) setIsPlatformAdmin(false)
      } finally {
        if (!cancelled) setIsReady(true)
      }
    }
    check()
    // Re-checa em mudança de auth
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      check()
    })
    return () => { cancelled = true; sub.subscription.unsubscribe() }
  }, [])

  // Se o usuário deixar de ser platform admin, limpa qualquer impersonation
  useEffect(() => {
    if (isReady && !isPlatformAdmin && viewingCompanyId) {
      try {
        localStorage.removeItem(STORAGE_KEY)
        localStorage.removeItem(STORAGE_NAME)
      } catch { /* noop */ }
      setViewingCompanyId(null)
      setViewingCompanyName(null)
    }
  }, [isReady, isPlatformAdmin, viewingCompanyId])

  const enterAsCompany = useCallback((companyId: string, companyName: string) => {
    try {
      localStorage.setItem(STORAGE_KEY, companyId)
      localStorage.setItem(STORAGE_NAME, companyName)
      localStorage.setItem(STORAGE_SINCE, String(Date.now()))
      // Limpa obra ativa para forçar seleção dentro da empresa visualizada
      localStorage.removeItem('cpro_ativa')
      localStorage.removeItem('cz:obra_id')
      localStorage.setItem('cz:company_id', companyId)
    } catch { /* noop */ }
    setViewingCompanyId(companyId)
    setViewingCompanyName(companyName)
    // Auditoria ANTES do reload (senão o registro se perde), depois navega.
    // Força reload completo para que o AuthContext recarregue obras/empresa
    // usando o ID efetivo (impersonado) durante a inicialização
    void auditImpersonation('enter', companyId, companyName).finally(() => {
      window.location.assign('/')
    })
  }, [])

  const exitImpersonation = useCallback(() => {
    const exitedId = viewingCompanyId
    const exitedName = viewingCompanyName
    try {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(STORAGE_NAME)
      localStorage.removeItem(STORAGE_SINCE)
      localStorage.removeItem('cpro_ativa')
      localStorage.removeItem('cz:obra_id')
      localStorage.removeItem('cz:company_id')
    } catch { /* noop */ }
    setViewingCompanyId(null)
    setViewingCompanyName(null)
    // Reload para reinicializar contexto com a empresa real do Super Admin
    void auditImpersonation('exit', exitedId, exitedName).finally(() => {
      window.location.assign('/superadmin')
    })
  }, [viewingCompanyId, viewingCompanyName])

  return (
    <Ctx.Provider value={{
      viewingCompanyId,
      viewingCompanyName,
      isPlatformAdmin,
      isReady,
      enterAsCompany,
      exitImpersonation,
    }}>
      {children}
    </Ctx.Provider>
  )
}
